import { useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Heart, ThumbsUp, Sparkles, MapPin } from "lucide-react";
import { toggleFavorite, isFavorite } from "@/api";
import { useLang } from "@/i18n";

export function WaveDivider({ fill = "#F3E4C9", flip = false, className = "" }) {
  return (
    <div className={`w-full overflow-hidden leading-none ${flip ? "rotate-180" : ""} ${className}`} aria-hidden="true">
      <svg viewBox="0 0 1200 60" preserveAspectRatio="none" className="w-full h-[40px] md:h-[60px] block">
        <path d="M0,30 C200,60 400,0 600,30 C800,60 1000,0 1200,30 L1200,60 L0,60 Z" fill={fill} />
      </svg>
    </div>
  );
}

export function MarqueeRibbon({ items, className = "bg-mare text-calce" }) {
  return (
    <div className={`py-3 ${className}`} data-testid="marquee">
      <Marquee speed={30} gradient={false}>
        {items.map((txt, i) => (
          <span key={i} className="mx-6 font-heading font-semibold tracking-wide flex items-center gap-6 text-sm uppercase">
            {txt} <span className="text-terracotta">★</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

export function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function SectionHead({ kicker, title, sub, align = "left" }) {
  return (
    <Reveal className={`mb-10 ${align === "center" ? "text-center" : ""}`}>
      {kicker && <p className="text-xs tracking-[0.2em] uppercase font-bold text-terracotta mb-3">{kicker}</p>}
      <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-grafite">{title}</h2>
      {sub && <p className="mt-3 text-grafite/70 max-w-2xl text-base leading-relaxed">{sub}</p>}
    </Reveal>
  );
}

export function TagBadge({ children, color = "#3B6E4E" }) {
  return (
    <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ background: color }}>
      {children}
    </span>
  );
}

export function TopPickBadge() {
  const { t } = useLang();
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold bg-sabbia text-grafite shadow-sm">
      <Sparkles className="w-3 h-3 text-terracotta" /> {t("badge.top")}
    </span>
  );
}

export function HeartButton({ slug, size = "md" }) {
  const { t } = useLang();
  const [liked, setLiked] = useState(isFavorite(slug));
  const [thanks, setThanks] = useState(false);
  const dims = size === "md" ? "w-10 h-10" : "w-9 h-9";
  return (
    <span className="relative inline-flex">
      {thanks && (
        <motion.span
          data-testid={`like-thanks-${slug}`}
          initial={{ opacity: 0, y: 8, scale: 0.8 }}
          animate={{ opacity: 1, y: -34, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: "spring", stiffness: 380, damping: 22 }}
          className="absolute -top-1 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap rounded-full bg-terracotta text-white text-[11px] font-bold px-3 py-1.5 shadow-lg pointer-events-none inline-flex items-center gap-1"
        >
          <ThumbsUp className="w-3 h-3" /> {t("like.thanks")}
        </motion.span>
      )}
      <motion.button
        data-testid={`like-${slug}`}
        aria-label={liked ? t("common.remove") : t("common.like")}
        aria-pressed={liked}
        whileTap={{ scale: 0.75 }}
        transition={{ type: "spring", stiffness: 500, damping: 15 }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const added = toggleFavorite(slug);
          setLiked(added);
          if (added) {
            setThanks(true);
            setTimeout(() => setThanks(false), 1700);
          }
        }}
        className={`${dims} rounded-full flex items-center justify-center shadow-md transition-colors ${
          liked ? "bg-terracotta text-white" : "bg-white/90 text-grafite hover:text-terracotta"
        }`}
      >
        <ThumbsUp className={`w-5 h-5 ${liked ? "fill-white" : ""}`} />
      </motion.button>
    </span>
  );
}

export function EmptyState({ text }) {
  return (
    <div data-testid="empty-state" className="text-center py-20">
      <MapPin className="w-10 h-10 mx-auto text-turchese mb-4" />
      <p className="font-accent text-2xl text-grafite/70">{text}</p>
    </div>
  );
}
